/**
 * Epoch-millisecond coercion shared by the attention / review selectors.
 *
 * Firestore documents written by four producers mix shapes: epoch seconds,
 * epoch milliseconds, ISO strings, Date objects and Firestore Timestamps.
 * Lives in its own module so `suspiciousAnimals.js` and `healthReview.js` can
 * both use it without importing each other.
 */

function normalizeEpochNumberToMs(value) {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		return null;
	}

	// Heuristic: seconds timestamps are ~1e9, milliseconds are ~1e12+
	return value < 1e12 ? value * 1000 : value;
}

/**
 * Converts supported timestamp shapes to epoch milliseconds.
 * @param {unknown} value - Number (s or ms), Date, ISO string or Firestore Timestamp
 * @returns {number|null} Epoch milliseconds, or null when unreadable
 */
export function getTimestampInMillis(value) {
	if (!value) {
		return null;
	}

	if (typeof value === "number") {
		return normalizeEpochNumberToMs(value);
	}

	if (value instanceof Date) {
		const ms = value.getTime();
		return Number.isNaN(ms) ? null : ms;
	}

	if (typeof value === "string") {
		const parsed = Date.parse(value);
		return Number.isNaN(parsed) ? null : parsed;
	}

	if (typeof value === "object") {
		if (typeof value.toMillis === "function") {
			return value.toMillis();
		}

		if (typeof value.toDate === "function") {
			return value.toDate().getTime();
		}

		if (
			Object.prototype.hasOwnProperty.call(value, "seconds") &&
			typeof value.seconds === "number"
		) {
			const nanoseconds =
				typeof value.nanoseconds === "number" ? value.nanoseconds : 0;
			return value.seconds * 1000 + Math.floor(nanoseconds / 1e6);
		}
	}

	return null;
}
